import React from "react";
import { motion } from "framer-motion";
import {
  Check,
  ChevronRight,
  ShoppingCart,
  ChefHat,
  Car,
  Clock,
  Brush,
  Heart,
  PartyPopper,
  Sparkles,
  Droplets,
  UtensilsCrossed,
  Package,
  TrendingUp,
} from "lucide-react";
import UNSPLASH from "../unsplashImages";

const ICON_MAP = {
  grocery_delivery: ShoppingCart,
  private_chef: ChefHat,
  airport_ride: Car,
  car_rental: Car,
  early_checkin: Clock,
  late_checkout: Clock,
  mid_stay_cleaning: Brush,
  massage: Heart,
  arrival_event: PartyPopper,
  wellness_therapy: Droplets,
  restaurant_reservations: UtensilsCrossed,
  other: Sparkles,
};

const IMAGE_MAP = {
  private_chef: UNSPLASH.services.privateChef,
  airport_ride: UNSPLASH.services.airportTransfer,
  car_rental: UNSPLASH.services.airportTransfer,
  massage: UNSPLASH.services.massage,
  arrival_event: UNSPLASH.services.arrivalEvent,
  grocery_delivery: UNSPLASH.services.groceryDelivery,
  wellness_therapy: UNSPLASH.services.ivTherapy,
  restaurant_reservations: UNSPLASH.services.restaurantReservations,
  early_checkin: UNSPLASH.lifestyle.luxuryInterior,
  late_checkout: UNSPLASH.lifestyle.poolside,
  mid_stay_cleaning: UNSPLASH.lifestyle.kitchen,
  other: UNSPLASH.services.customRequest,
};

const POPULAR_SERVICES = new Set([
  "private_chef",
  "early_checkin",
  "late_checkout",
  "massage",
]);

const DISPLAY_NAME_MAP = {
  private_chef: "Private Chef",
  massage: "In-Home Massage",
  arrival_event: "Event Setup",
  airport_ride: "Airport Transfer",
  car_rental: "Car Rentals",
  early_checkin: "Early Check-In",
  late_checkout: "Late Check-Out",
  mid_stay_cleaning: "Mid-Stay Cleaning",
  wellness_therapy: "Wellness Therapy",
  restaurant_reservations: "Restaurant Reservations",
  grocery_delivery: "Pre-Arrival Grocery Delivery",
  other: "Custom Requests",
};

const SERVICE_COPY = {
  early_checkin: {
    guestValue: "Guests arrive on their schedule, not yours",
    hostValue: "Charge $25–$100+ per early arrival",
  },
  late_checkout: {
    guestValue: "A relaxed departure makes the whole trip better",
    hostValue: "Monetize unused hours between bookings",
  },
  mid_stay_cleaning: {
    guestValue: "A fresh, clean space mid-trip without asking",
    hostValue: "Offer premium housekeeping as a paid upgrade",
  },
  private_chef: {
    guestValue: "In-home dining crafted by a local chef",
    hostValue: "Earn from every booking — zero work for you",
  },
  airport_ride: {
    guestValue: "Door-to-door transfers, no surge pricing",
    hostValue: "Seamless arrival experience that earns 5-star reviews",
  },
  car_rental: {
    guestValue: "Convenient vehicle rental delivered to the property",
    hostValue: "High-demand add-on that upgrades the stay",
  },
  massage: {
    guestValue: "Licensed therapists come to the property",
    hostValue: "High-ticket wellness add-on guests love",
  },
  wellness_therapy: {
    guestValue: "IV drips and wellness treatments in-home",
    hostValue: "Premium wellness service with strong margins",
  },
  arrival_event: {
    guestValue: "Balloons, flowers, and surprises at the door",
    hostValue: "Celebration setups drive repeat bookings",
  },
  restaurant_reservations: {
    guestValue: "Priority tables at the best local restaurants",
    hostValue: "Concierge-level touch that sets you apart",
  },
  grocery_delivery: {
    guestValue: "A stocked fridge waiting when they walk in",
    hostValue: "Already included — builds guest loyalty from day one",
  },
  other: {
    guestValue: "Anything they need — just ask",
    hostValue: "Catch-all service for unique guest requests",
  },
};

function getIcon(key) {
  return ICON_MAP[key] || Package;
}

function getCopy(key) {
  return SERVICE_COPY[key] || SERVICE_COPY.other;
}

function getImage(key) {
  return IMAGE_MAP[key] || null;
}

function getDisplayName(key, fallbackName) {
  return DISPLAY_NAME_MAP[key] || fallbackName || key;
}

export const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 260, damping: 24 },
  },
};

export default function ServiceCard({
  service,
  hostService,
  isSubscribed,
  isFree,
  perServicePrice,
  onToggle,
  onConfigure,
  isCustomLocked,
}) {
  const enabled = !!hostService?.enabled;
  const Icon = getIcon(service.key);
  const copy = getCopy(service.key);
  const image = getImage(service.key);
  const displayName = getDisplayName(service.key, service.name);
  const isPopular = POPULAR_SERVICES.has(service.key);

  if (isFree) {
    return (
      <motion.div
        variants={cardVariants}
        whileHover={{ y: -3 }}
        className="relative overflow-hidden border border-emerald-200 rounded-2xl bg-white transition-shadow hover:shadow-md"
      >
        {/* Image strip */}
        {image && (
          <div className="relative h-28 overflow-hidden">
            <img
              src={image}
              alt={displayName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-2.5 left-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                <Icon size={16} className="text-emerald-600" strokeWidth={2} />
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm">
                <Check size={9} strokeWidth={3} />
                Included Free
              </span>
            </div>
          </div>
        )}
        <div className="p-4">
          <h3 className="text-[15px] font-bold text-gray-900 mb-1">{displayName}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{copy.guestValue}</p>
          <p className="text-xs text-emerald-700 font-medium mt-2 flex items-center gap-1">
            <Check size={11} className="text-emerald-500" strokeWidth={2.5} />
            {copy.hostValue}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={cardVariants}
      whileHover={isCustomLocked ? {} : { y: -4 }}
      className={`relative overflow-hidden rounded-2xl transition-all duration-200 ${
        isCustomLocked
          ? "bg-gray-50 border border-gray-200 opacity-75"
          : enabled
          ? "bg-white border-2 border-primary-500/20 shadow-bellhopt"
          : "bg-white border border-gray-200 hover:shadow-md"
      }`}
    >
      {/* Image banner */}
      {image && (
        <div className="relative h-36 overflow-hidden">
          <img
            src={image}
            alt={displayName}
            className={`w-full h-full object-cover transition-all duration-300 ${
              isCustomLocked ? "saturate-0 opacity-60" : enabled ? "" : "saturate-[0.85]"
            }`}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

          {/* Icon badge on image */}
          <div className="absolute bottom-2.5 left-3">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-sm transition-colors ${
                isCustomLocked
                  ? "bg-gray-400 text-white"
                  : enabled
                  ? "bg-primary-500 text-white"
                  : "bg-white/90 backdrop-blur-sm text-gray-600"
              }`}
            >
              <Icon size={17} strokeWidth={1.8} />
            </div>
          </div>

          {/* Badges on image */}
          <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
            {isCustomLocked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-800/70 text-white text-[10px] font-bold shadow-sm backdrop-blur-sm">
                Subscription required
              </span>
            )}
            {!isCustomLocked && isPopular && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-white text-[10px] font-bold shadow-sm">
                <TrendingUp size={9} strokeWidth={2.5} />
                Popular
              </span>
            )}
            {!isCustomLocked && enabled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold shadow-sm">
                <span className="w-1.5 h-1.5 bg-white rounded-full" />
                Active
              </span>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className={`text-[15px] font-bold leading-snug ${isCustomLocked ? "text-gray-500" : "text-gray-900"}`}>
            {displayName}
          </h3>

          {isCustomLocked ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-200 text-gray-500 text-[10px] font-semibold flex-shrink-0">
              Drafted
            </span>
          ) : (
            <button
              onClick={() => onToggle(service.key, !enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                enabled ? "bg-primary-500" : "bg-gray-300"
              }`}
              aria-label={enabled ? "Disable" : "Enable"}
            >
              <motion.span
                className="inline-block h-5 w-5 rounded-full bg-white shadow"
                animate={{ x: enabled ? 21 : 2 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
          )}
        </div>

        <p className={`text-sm leading-relaxed ${isCustomLocked ? "text-gray-400" : "text-gray-500"}`}>
          {isCustomLocked ? "Available once Concierge is activated" : copy.guestValue}
        </p>

        {!isCustomLocked && (
          <div
            className={`text-xs font-medium mt-2 flex items-center gap-1 ${
              enabled ? "text-primary-600" : "text-gray-400"
            }`}
          >
            <TrendingUp size={11} strokeWidth={2} />
            {copy.hostValue}
          </div>
        )}

        {/* Footer: pricing + configure */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          {!isCustomLocked && perServicePrice > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
              + ${perServicePrice}/home
            </span>
          )}

          {isCustomLocked && (
            <span className="text-xs text-gray-400">Unlock with subscription</span>
          )}

          {!isCustomLocked && isSubscribed && (
            <button
              onClick={() => onConfigure(service)}
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-primary-500 transition-colors ml-auto"
            >
              Configure
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export { getIcon, getCopy, getDisplayName, SERVICE_COPY, ICON_MAP, IMAGE_MAP, DISPLAY_NAME_MAP };
